import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { LOCAL_STORAGE, useLocalStorage } from "@useLocalStorage";
import { postAnalytics } from "@mainaction";

export default function usePostAnalytics(session, defaultLogType = "entry") {
  const urlParams = useParams();
  const dispatch = useDispatch();
  const { setLocalStorage, getLocalStorage } = useLocalStorage();

  function getTicketCode(obj) {
    if (obj.ticketcode) return obj.ticketcode;
    if (typeof obj["*"] === "string" && obj["*"].startsWith("tickets/")) {
      return obj["*"].slice(8); // Remove "tickets/" (8 characters)
    }
    return ""; // Default return if both are missing or invalid
  }

  function getFormid(obj) {
    const path = obj["*"];
    const prefix = "register/";

    if (typeof path === "string" && path.startsWith(prefix)) {
      return path.slice(prefix.length);
    }

    return null;
  }

  function getLinkType(obj) {
    const path = obj["*"];
    if (typeof path === "string") {
      if (path.startsWith("tickets/")) {
        return "ticket";
      } else if (path.startsWith("register/")) {
        return "registration";
      }
    }
    return null;
  }

  const postAffiliationAnalytics = useCallback(
    (customLogType) => {
      const trackingcode = new URLSearchParams(window.location.search).get(
        "affiliation"
      );
      const storedAffiliation =
        getLocalStorage(LOCAL_STORAGE.AFFILIATION_CODE) || {};

      const lastTrackingCode = storedAffiliation?.trackingcode;
      const storedTypes = storedAffiliation?.types || []; // Array to store types used
      const redemption = storedAffiliation?.redemption;
      const logType = customLogType || defaultLogType;

      const currentType = getLinkType(urlParams);
      const ticketcode = getTicketCode(urlParams);
      const formid = getFormid(urlParams);

      const isNewTrackingCode =
        trackingcode && trackingcode !== lastTrackingCode;
      const isNewTypeForSameCode =
        trackingcode &&
        trackingcode === lastTrackingCode &&
        currentType &&
        !storedTypes.includes(currentType);
      const isRedemptionLog = logType === "redemption" && !redemption;

      // Post analytics if it's a new tracking code, new type for same code, or redemption log
      if (isNewTrackingCode || isNewTypeForSameCode || isRedemptionLog) {
        dispatch(
          postAnalytics(
            session,
            {
              ticketcode,
              formid,
              trackingcode,
              options: { type: logType },
            },
            () => {}
          )
        );

        // Update localStorage with type tracking
        let updatedTypes = [...storedTypes];

        if (isNewTrackingCode) {
          // New tracking code - reset types array with current type
          updatedTypes = currentType ? [currentType] : [];
        } else if (isNewTypeForSameCode && currentType) {
          // Same tracking code but new type - add to types array
          updatedTypes = [...storedTypes, currentType];
        }

        setLocalStorage(
          LOCAL_STORAGE.AFFILIATION_CODE,
          {
            trackingcode,
            types: updatedTypes,
            redemption: trackingcode === lastTrackingCode ? redemption : false,
          },
          false
        );
      }
    },
    [session, urlParams]
  );

  //socialboost analytics in progress
  const postSocialBoostAnalytics = useCallback(() => {}, [session]);

  useEffect(() => {
    postAffiliationAnalytics();
  }, []);

  return postAffiliationAnalytics; // Tigger for manually posting the log.
}
